import React from "react";
import _ from "lodash";
import { IFieldProps } from ".";
import {
    createLocalUrlFromS3,
    IS3ExternalData
} from "customHooks/useCompleoForm/Inputs/File/File";
import ApplicantCVPreview from "Pages/Applicant/ApplicantNewEdit/ApplicantCVPreview";
import { Box } from "@material-ui/core";
import Loading from "customHooks/useCompleoForm/helpers/Loading";
import { Alert } from "@material-ui/lab";
import useShowMessage from "customHooks/useShowMessage/useShowMessage";
import FieldFile from "./FieldFile";

const FieldViewFile = (props: IFieldProps) => {
    const { fieldData, fieldName, t, propsToAdd } = props;
    const showMessage = useShowMessage();

    const showMessageRef = React.useRef(showMessage);
    const tRef = React.useRef(t);
    React.useEffect(() => {
        showMessageRef.current = showMessage;
        tRef.current = t;
    }, [showMessage, t]);

    const fieldValue = _.get(fieldData, fieldName);

    const fileKey: string | undefined =
        typeof fieldValue === "string"
            ? fieldValue
            : fieldValue?.key || fieldValue?.FileInfo?.key;
    const [localUrl, setLocalUrl] = React.useState("");
    const [retryAfterUpdate, setRetryAfterUpdate] = React.useState(false);
    const [error, setError] = React.useState(false);
    const [errorConvertPDF, setErrorConvertPDF] = React.useState(false);

    const getFileFromS3 = React.useCallback(
        (ignoreRetry: boolean) => {
            if (!fileKey) return;
            const externalData:
                | IS3ExternalData
                | undefined = propsToAdd.evaluationId
                ? {
                      evaluationId: propsToAdd.evaluationId
                  }
                : undefined;

            createLocalUrlFromS3(fileKey, true, externalData).then(
                (localUrlData) => {
                    setLocalUrl(localUrlData.url);
                },
                () => {
                    const cvFileName = String(fileKey ?? "");

                    if (!ignoreRetry) {
                        if (!cvFileName.includes(".pdf")) {
                            showMessageRef.current({
                                enabled: true,
                                message: tRef.current("convertingFileMessage"),
                                timeout: 7000,
                                typeMessage: "warning",
                                snackBarOrign: {
                                    horizontal: "right",
                                    vertical: "top"
                                }
                            });
                        }
                        setTimeout(function () {
                            setRetryAfterUpdate(true);
                        }, 5000);
                    } else {
                        if (!cvFileName.includes(".pdf")) {
                            setErrorConvertPDF(true);
                        } else {
                            setError(true);
                        }
                    }
                }
            );
        },
        [fileKey, propsToAdd.evaluationId]
    );

    React.useEffect(() => {
        if (fileKey) {
            setLocalUrl("");
            getFileFromS3(false);
        } else {
            setLocalUrl("");
        }
    }, [fileKey, getFileFromS3]);

    React.useEffect(() => {
        if (fileKey && retryAfterUpdate) {
            getFileFromS3(true);
            setRetryAfterUpdate(false);
        }
    }, [fileKey, retryAfterUpdate, getFileFromS3]);

    // console.log("localUrl", localUrl);

    const cvFileName = React.useMemo(() => {
        const rawName = _.get(fieldData, "name");

        const safeBase = String(rawName || "CV")
            .trim()
            .replace(/[\\/]+/g, "-");

        return safeBase.toLowerCase().endsWith(".pdf")
            ? safeBase
            : `${safeBase}.pdf`;
    }, [fieldData]);

    if (localUrl) {
        return (
            <Box width="100%">
                <ApplicantCVPreview
                    url={localUrl}
                    isLocalStreamUrl={true}
                    fileName={cvFileName}
                />
            </Box>
        );
    } else if (fieldValue?.key) {
        if (!error && !errorConvertPDF) {
            return (
                <Box width="100%">
                    <Loading />
                </Box>
            );
        } else if (errorConvertPDF) {
            return <FieldFile {...props} disableSearch />;
        } else {
            return (
                <Box width="100%" mt={2}>
                    <Alert severity="error">{t("errorFileMsg")}</Alert>
                </Box>
            );
        }
    } else {
        return <Box width="100%">{t("applicantWithoutCVMessage")}</Box>;
    }
};

export default FieldViewFile;
